import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Loader2, Paperclip, Send } from "lucide-react";
import { connectParityApi, normalizeUserId, type ConversationScopeKind } from "@/api/connectApi";
import { PollComposerDialog } from "../polls/PollComposerDialog";
import { useAccessToken } from "@/auth/useAccessToken";
import { EmojiPickerTrigger } from "../EmojiPicker";
import { renderMarkdown } from "@/lib/markdown";
import { renderWithMentions, type MentionPick } from "@/lib/mentions";
import type { DriveShareSource } from "@/lib/drive";
import { cn } from "@/lib/utils";
import { ComposerAttachments, DropOverlay } from "../files/ComposerAttachments";
import { filesFromPaste, useFileDropZone, type DriveAttachments } from "../files/useDriveAttachments";
import { ChatAvatar } from "./chatUi";

/** Who a send shares its files with — resolved at send time so a late-loading member list is still used. */
export interface ChatShareTarget {
    emails: string[];
    ownerEmail: string;
    source: DriveShareSource;
    sourceRef: string;
}

/** Someone who can be @mentioned: the chat's members are offered first. */
export interface MentionCandidate {
    userId: string;
    name: string;
    email: string;
}

interface MentionOption extends MentionCandidate {
    /** Not in this chat — mentioning them highlights the name but notifies no one. */
    outside: boolean;
}

/** The @token being typed: from the "@" up to the caret. */
interface MentionQuery {
    start: number;
    query: string;
}

/**
 * Composer for 1:1 and group chats. These had a bare textarea — no emoji, no
 * formatting, a fixed height — while channels had the full composer. It grows
 * with the text, sends on Enter (Shift+Enter for a new line), inserts emoji at
 * the cursor, renders Markdown (**bold**, _italic_, `code`, lists, links) and
 * tells the others you're typing. A failed send keeps your text.
 *
 * Files (attach button, paste, or drop) upload to the sender's Drive as soon
 * as they're attached and are shared with the chat on send — the Teams model.
 * The box clears the moment you press Send so you can keep typing while files
 * finish uploading; if the send fails the text and files come back.
 *
 * Typing "@" opens a people picker (the chat's members first, then the
 * directory); a pick is sent as a mention chip and the person — if they're in
 * the chat — gets an Activity notification and an "@" on their Chat list.
 */
export function ChatComposer({
    placeholder, disabled, onSend, onTyping, attachments, resolveShare, mentionCandidates, poll, requireRecipients = true
}: {
    placeholder: string;
    disabled?: boolean;
    onSend: (html: string) => Promise<void>;
    onTyping?: (started: boolean) => void;
    attachments?: DriveAttachments;
    resolveShare?: () => Promise<ChatShareTarget>;
    /** The chat's other members; enables @mentions. */
    mentionCandidates?: MentionCandidate[];
    /** Where a poll created from the composer's Poll button belongs; no button without it. */
    poll?: { scope: ConversationScopeKind; conversationId: string };
    /** Refuse to send files when there's nobody else to share them with (chats); a channel may be just you. */
    requireRecipients?: boolean;
}) {
    const token = useAccessToken();
    const [pollOpen, setPollOpen] = useState(false);
    const [text, setText] = useState("");
    // The text as last rendered — a failed send checks it before restoring.
    const textRef = useRef(text);
    textRef.current = text;
    const [inFlight, setInFlight] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const ref = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const typingRef = useRef(false);
    const idleTimer = useRef<number | undefined>(undefined);
    const onTypingRef = useRef(onTyping);
    onTypingRef.current = onTyping;
    const drop = useFileDropZone((files) => attachments?.addFiles(files));

    // ── @mentions ──────────────────────────────────────────────────────────
    const listId = useId();
    const [picks, setPicks] = useState<MentionPick[]>([]);
    const [mention, setMention] = useState<MentionQuery | null>(null);
    const [highlight, setHighlight] = useState(0);
    const [dirQuery, setDirQuery] = useState("");
    const mentionsOn = Boolean(mentionCandidates);

    // Directory search, debounced, once two letters are typed after "@".
    useEffect(() => {
        const q = mention?.query.trim() ?? "";
        const t = window.setTimeout(() => setDirQuery(q.length >= 2 ? q : ""), 200);
        return () => window.clearTimeout(t);
    }, [mention?.query]);
    const directory = useQuery({
        queryKey: ["connect-dir-search", dirQuery, token],
        queryFn: () => connectParityApi.searchDirectory(dirQuery, token),
        enabled: Boolean(mentionsOn && token && dirQuery.length >= 2),
        staleTime: 30_000
    });

    const options = useMemo<MentionOption[]>(() => {
        if (!mention || !mentionCandidates) return [];
        const q = mention.query.toLowerCase();
        const members = mentionCandidates
            .filter((c) => !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
            .slice(0, 6)
            .map((c) => ({ ...c, outside: false }));
        const memberIds = new Set(mentionCandidates.map((c) => normalizeUserId(c.userId)));
        const others = q.length >= 2
            ? (directory.data ?? [])
                .filter((u) => !memberIds.has(normalizeUserId(u.userId)))
                .slice(0, 5)
                .map((u) => ({ userId: u.userId, name: u.displayName || u.email, email: u.email, outside: true }))
            : [];
        return [...members, ...others];
    }, [mention, mentionCandidates, directory.data]);
    const menuOpen = mention !== null && options.length > 0;

    useEffect(() => { setHighlight(0); }, [mention?.start, options.length]);

    /** Find the "@token" right before the caret (none inside a word or an email address). */
    const rescan = (value: string, caret: number) => {
        if (!mentionsOn) return;
        const before = value.slice(Math.max(0, caret - 50), caret);
        const match = /(^|[\s(])@([^\s@]{0,40})$/.exec(before);
        if (!match) { setMention(null); return; }
        const start = caret - match[2]!.length - 1;
        setMention({ start, query: match[2]! });
    };

    const pick = (option: MentionOption) => {
        const el = ref.current;
        if (!mention || !el) return;
        const caret = el.selectionStart ?? text.length;
        const insertion = `@${option.name} `;
        const next = text.slice(0, mention.start) + insertion + text.slice(caret);
        setText(next);
        setPicks((prev) => prev.some((p) => normalizeUserId(p.userId) === normalizeUserId(option.userId))
            ? prev
            : [...prev, { userId: option.userId, name: option.name }]);
        setMention(null);
        window.requestAnimationFrame(() => {
            el.focus();
            const at = mention.start + insertion.length;
            el.setSelectionRange(at, at);
        });
    };

    // Grow with the text, up to about eight lines.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    }, [text]);

    const stopTyping = () => {
        if (idleTimer.current !== undefined) window.clearTimeout(idleTimer.current);
        if (typingRef.current) {
            typingRef.current = false;
            onTypingRef.current?.(false);
        }
    };

    useEffect(() => () => stopTyping(), []);

    const onChange = (value: string, caret: number) => {
        setText(value);
        setError(null);
        rescan(value, caret);
        if (!onTypingRef.current) return;
        if (!typingRef.current && value.trim()) {
            typingRef.current = true;
            onTypingRef.current(true);
        }
        if (idleTimer.current !== undefined) window.clearTimeout(idleTimer.current);
        idleTimer.current = window.setTimeout(stopTyping, 3000);
    };

    const pendingFiles = attachments?.files.filter((f) => !f.locked) ?? [];

    const send = async () => {
        const value = text.trim();
        if ((!value && pendingFiles.length === 0) || disabled) return;
        const sentPicks = picks;
        setText("");
        setPicks([]);
        setMention(null);
        stopTyping();
        setError(null);
        setInFlight((n) => n + 1);
        let keys: string[] = [];
        try {
            let markers = "";
            if (attachments && pendingFiles.length > 0) {
                if (!resolveShare) throw new Error("Files can't be shared here.");
                const share = await resolveShare();
                const prepared = await attachments.prepareSend({ ...share, requireRecipients });
                markers = prepared.markersHtml;
                keys = prepared.keys;
            }
            // Picked people's "@Name" become mention chips; everything else is Markdown.
            await onSend((value ? renderWithMentions(value, sentPicks, renderMarkdown) : "") + markers);
            if (keys.length > 0) attachments?.commit(keys);
            ref.current?.focus();
        } catch (err) {
            if (keys.length > 0) attachments?.release(keys);
            // Put the text (and its mentions) back unless they've already started a new message.
            if (!textRef.current.trim()) {
                setText(value);
                setPicks(sentPicks);
            }
            const msg = err instanceof Error ? err.message : "";
            setError(msg && !msg.startsWith("Connect BFF")
                ? msg
                : "Couldn't send — your message is still here. Try again.");
        } finally {
            setInFlight((n) => n - 1);
        }
    };

    const insertEmoji = (emoji: string) => {
        const el = ref.current;
        if (!el) { setText((t) => t + emoji); return; }
        const start = el.selectionStart ?? text.length;
        const end = el.selectionEnd ?? text.length;
        setText(text.slice(0, start) + emoji + text.slice(end));
        window.requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + emoji.length, start + emoji.length);
        });
    };

    const activeOption = menuOpen ? `${listId}-opt-${highlight}` : undefined;

    return (
        <div className="relative shrink-0 px-3 pb-3 pt-1 sm:px-4 sm:pb-3.5" {...(attachments ? drop.dropProps : {})}>
            {attachments && drop.isDragging ? <DropOverlay /> : null}
            {menuOpen ? (
                <ul
                    id={listId}
                    role="listbox"
                    aria-label="People to mention"
                    className="absolute bottom-full left-3 z-30 mb-1 max-h-72 w-80 max-w-[calc(100%-1.5rem)] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
                >
                    {options.map((o, i) => {
                        const firstOutside = o.outside && (i === 0 || !options[i - 1]!.outside);
                        return (
                            <li key={`${o.outside ? "dir" : "m"}-${o.userId}`} role="presentation">
                                {firstOutside ? (
                                    <div className="px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" role="presentation">
                                        Others in your organisation
                                    </div>
                                ) : null}
                                <div
                                    id={`${listId}-opt-${i}`}
                                    role="option"
                                    aria-selected={i === highlight}
                                    // mousedown keeps focus (and the caret) in the textarea
                                    onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                                    onMouseEnter={() => setHighlight(i)}
                                    className={cn(
                                        "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                                        i === highlight ? "bg-accent" : "hover:bg-accent/60"
                                    )}
                                >
                                    <ChatAvatar name={o.name} size="sm" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate">{o.name}</span>
                                        <span className="block truncate text-[11px] text-muted-foreground">
                                            {o.outside ? "Not in this chat — won't be notified" : o.email}
                                        </span>
                                    </span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
            {/* The mock-up's compose box: an 8px-rounded card with a strong outline. */}
            <div className="rounded-lg border border-[var(--connect-line-strong)] bg-card px-2 py-1 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
                {attachments && attachments.files.length > 0 ? (
                    <div className="px-1 pb-1 pt-1.5">
                        <ComposerAttachments attachments={attachments} />
                    </div>
                ) : null}
                <div className="flex items-end gap-1">
                    <textarea
                        ref={ref}
                        value={text}
                        rows={1}
                        onChange={(e) => onChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
                        onClick={(e) => rescan(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
                        onKeyUp={(e) => {
                            if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Home" || e.key === "End") {
                                rescan(e.currentTarget.value, e.currentTarget.selectionStart ?? 0);
                            }
                        }}
                        onBlur={() => setMention(null)}
                        onKeyDown={(e) => {
                            if (menuOpen) {
                                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                    e.preventDefault();
                                    const n = options.length;
                                    setHighlight((h) => (e.key === "ArrowDown" ? (h + 1) % n : (h - 1 + n) % n));
                                    return;
                                }
                                if ((e.key === "Enter" || e.key === "Tab") && !e.nativeEvent.isComposing) {
                                    e.preventDefault();
                                    const o = options[highlight] ?? options[0];
                                    if (o) pick(o);
                                    return;
                                }
                                if (e.key === "Escape") {
                                    e.preventDefault();
                                    setMention(null);
                                    return;
                                }
                            }
                            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                                e.preventDefault();
                                void send();
                            }
                        }}
                        onPaste={(e) => {
                            if (!attachments) return;
                            const pasted = filesFromPaste(e);
                            if (pasted.length === 0) return;
                            e.preventDefault();
                            attachments.addFiles(pasted);
                        }}
                        placeholder={placeholder}
                        aria-label={placeholder}
                        aria-autocomplete={mentionsOn ? "list" : undefined}
                        aria-controls={menuOpen ? listId : undefined}
                        aria-activedescendant={activeOption}
                        disabled={disabled}
                        className="max-h-[200px] min-h-[36px] flex-1 resize-none bg-transparent px-1 py-2 text-sm outline-none placeholder:text-muted-foreground"
                    />
                    {attachments ? (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) attachments.addFiles(e.target.files);
                                    e.target.value = "";
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={disabled}
                                aria-label="Attach files"
                                title="Attach files"
                                className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
                            >
                                <Paperclip className="h-4 w-4" />
                            </button>
                        </>
                    ) : null}
                    <EmojiPickerTrigger onSelect={insertEmoji} />
                    {poll ? (
                        <button
                            type="button"
                            onClick={() => setPollOpen(true)}
                            disabled={disabled}
                            aria-label="Create a poll"
                            title="Poll"
                            className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
                        >
                            <BarChart3 className="h-4 w-4" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => void send()}
                        disabled={(!text.trim() && pendingFiles.length === 0) || disabled}
                        aria-label="Send"
                        title="Send (Enter)"
                        className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:text-muted-foreground disabled:hover:bg-transparent"
                    >
                        {inFlight > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                </div>
            </div>
            {error ? (
                <p role="alert" className="mt-1 px-1 text-xs text-destructive">{error}</p>
            ) : (
                <p className="mt-1 hidden px-1 text-[10px] text-muted-foreground sm:block">
                    Enter to send · Shift+Enter for a new line{mentionsOn ? " · @ to mention" : ""} · **bold** _italic_ `code`{attachments ? " · paste or drop files to share" : ""}
                </p>
            )}
            {poll ? (
                <PollComposerDialog
                    open={pollOpen}
                    onOpenChange={setPollOpen}
                    scope={poll.scope}
                    conversationId={poll.conversationId}
                    onPost={(html) => onSend(html)}
                />
            ) : null}
        </div>
    );
}
